import { RefObject, useCallback, useEffect } from 'react';

import {
  LongPressCallback,
  LongPressCallbackMeta,
  LongPressEventType,
  LongPressOptions,
  useLongPress,
} from 'use-long-press';

interface UseClickOutsideProps {
  ref: RefObject<HTMLElement | null>;
  callback: () => void;
}

const useClickOutside = ({ ref, callback }: UseClickOutsideProps) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref?.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback]);
};

export default useClickOutside;

interface UseHandleLongPressProps {
  handleClickOutside: (event?: MouseEvent) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSetLongPress: (context: any) => void;
  handleLongPressStart: () => void;
  ref: RefObject<HTMLElement | null>;
}

const useHandleLongPress = <T extends HTMLElement>({
  handleClickOutside,
  handleSetLongPress,
  handleLongPressStart,
  ref,
}: UseHandleLongPressProps) => {
  useClickOutside({ ref, callback: handleClickOutside });

  const callback = useCallback(
    (_: React.MouseEvent<T> | React.TouchEvent<T>, meta: LongPressCallbackMeta<T>) => {
      if (meta.context) {
        handleSetLongPress(meta.context);
      }
    },
    [handleSetLongPress],
  );

  const bind = useLongPress<T>(
    callback as LongPressCallback<T, unknown>,
    {
      filterEvents: () => true,
      threshold: 800,
      captureEvent: true,
      cancelOnMovement: true,
      cancelOutsideElement: true,
      detect: LongPressEventType.Pointer,
      onStart: handleLongPressStart,
    } as LongPressOptions<T>,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlers = (context: any) => ({
    ...bind(context),
    ref,
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  });

  return { handlers };
};

export { useClickOutside, useHandleLongPress };
